import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { type ICardFilter } from 'shared/ui'
import { TableStore } from 'shared/ui/TableNew'
import { type PageLayoutStore } from 'shared/layout'
import { logger } from 'shared/lib'
import {
  type IParamsGetBroadcastContacts,
  type IResponseRecurringBroadcastById,
} from 'entities/Broadcast/api/types'
import { BroadcastRecurring } from 'entities/Broadcast'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { RowBroadcastRecurring } from 'entities/Broadcast/model/Table/RowBroadcastRecurring'
import { type RowBroadcastOneTime } from 'entities/Broadcast/model/Table/RowBroadcastOneTime'
import { BROADCAST_REFRESH_DELAY_IN_PROGRESS_INTERVAL } from 'pages/broadcasts/constants'

export class BroadcastDetailRecurringStore {
  private readonly _tableStore: TableStore<RowBroadcastOneTime>
  private readonly _broadcast: BroadcastRecurring
  private readonly _broadcastRow: RowBroadcastRecurring

  constructor(
    broadcastResponse: IResponseRecurringBroadcastById,
    private _pageLayoutStore: PageLayoutStore,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)

    this._tableStore = new TableStore({
      key: 'broadcast_detail_recurring',
    })
    this._broadcast = new BroadcastRecurring(broadcastResponse, this._entities)
    this._broadcastRow = new RowBroadcastRecurring(this._broadcast, this._entities, true)
  }

  init = () => {
    this._reactionStatus()
    return this.dispose
  }
  dispose = () => {
    if (this._statusTimeoutId) clearTimeout(this._statusTimeoutId)
    this._disposeStatus?.()
  }

  get tableStore() {
    return this._tableStore
  }

  get broadcast() {
    return this._broadcast
  }

  get broadcastRow() {
    return this._broadcastRow
  }

  get pageLayoutStore() {
    return this._pageLayoutStore
  }

  get name() {
    return this._broadcast.name
  }

  get status() {
    return this._broadcast.status
  }

  get type() {
    return this._broadcast.type
  }

  get failed_reason() {
    return this._broadcast.failed_reason
  }

  get statistics() {
    return this._broadcast.statistics
  }

  get filters(): ICardFilter<IParamsGetBroadcastContacts['filter']>[] {
    if (!this.statistics) return []
    return this.statistics?.filters
      ?.filter((filter) => filter.key !== 'skipped')
      .map((column) => ({ ...column, notInteractable: true }))
  }

  private _statusTimeoutId: NodeJS.Timeout | null = null
  private _handleStatus = () => {
    if (this.status === 'active') {
      if (this._statusTimeoutId) clearTimeout(this._statusTimeoutId)
      this._statusTimeoutId = setTimeout(async () => {
        try {
          const { data } = await this._entities.broadcastStore.getItemById(this.broadcast.id)
          if (data.status === 'recurring_template') {
            this.broadcast.syncOrigin(data)
            this._handleStatus()
          }
        } catch (e) {
          logger.error(e)
        }
      }, BROADCAST_REFRESH_DELAY_IN_PROGRESS_INTERVAL)
    } else {
      if (this._statusTimeoutId) clearTimeout(this._statusTimeoutId)
    }
  }
  private _disposeStatus: IReactionDisposer | null = null
  private _reactionStatus = () => {
    this._disposeStatus?.()
    this._disposeStatus = reaction(() => this.status, this._handleStatus, {
      fireImmediately: true,
    })
  }
}
